/**
 * Qué hacer cuando el backend rechaza el token en mitad de la navegación.
 *
 * El token puede dejar de servir antes de la fecha que lleva dentro: lo rechaza el backend, o el
 * usuario terminó con un token de invitado en una sección que exige sesión. Cuando eso pasaba, cada
 * pantalla lo traducía a su manera —la tabla salía vacía, jurados mostraba "refresque la página"— y
 * el usuario tenía que cerrar sesión e iniciar de nuevo para volver a la normalidad.
 *
 * Aquí se mira la respuesta: si el backend dijo que el token no sirve, se cierra la sesión y se
 * lleva al login. Un fallo de red o un error del servidor NO cuentan: esos se dejan pasar, porque
 * sacar al usuario por un problema pasajero es justo lo que hacía que la sesión "se expirara sola".
 */

import { ObjNotificaciones } from "../config/notificaciones";
import { exportState } from "../utils/exportState";
import { confirmarVigencia, tokenVencido } from "./vigenciaDelToken";

/** Adónde se manda al usuario a iniciar sesión otra vez. */
export const RUTA_LOGIN = "/estimuloscultura/login";

/** Lo que se espera antes de redirigir, para que alcance a leerse el aviso. */
const ESPERA_ANTES_DEL_LOGIN_MS = 2000;

/**
 * Así responden los backends cuando el token no sirve. Responden 400, no 401, así que el código de
 * estado por sí solo no alcanza para distinguirlos de un error de datos cualquiera.
 */
const MENSAJES_DE_TOKEN = [
  /token\s+invalido/i,
  /token\s+inválido/i,
  /no\s+hay\s+token/i,
  /token\s+invitado\s+invalido/i,
  /jwt\s+expired/i,
  /token\s+expirado/i,
  /sesión\s+expirada/i,
];

const mensajeDeLaRespuesta = (datos: any): string => {
  if (typeof datos === "string") return datos;
  return `${datos?.mensaje ?? datos?.message ?? datos?.error ?? ""}`;
};

/**
 * ¿El backend dijo que el token no sirve?
 *
 * Un 401 lo dice sin lugar a dudas. Un 400 o un 403 solo cuentan si el mensaje habla del token:
 * esos códigos también los usan las validaciones normales, y no se saca a nadie por eso.
 */
export const esSesionRechazada = (error: any): boolean => {
  const respuesta = error?.response;
  if (!respuesta) return false; // sin respuesta fue la red, no el token

  if (respuesta.status === 401) return true;
  if (respuesta.status !== 400 && respuesta.status !== 403) return false;

  const mensaje = mensajeDeLaRespuesta(respuesta.data);
  return MENSAJES_DE_TOKEN.some((patron) => patron.test(mensaje));
};

/** Evita repetir el aviso cuando varias peticiones fallan a la vez por el mismo token. */
let cerrandoSesion = false;

/** Solo para las pruebas: vuelve a dejar el aviso disponible. */
export const reiniciarCierreDeSesion = () => {
  cerrandoSesion = false;
};

/** Cierra la sesión, avisa y lleva al login. */
export const cerrarSesionYAvisar = (
  titulo = "Sesión expirada",
  detalle = "Su sesión ya no es válida, por favor inicie sesión nuevamente",
  /** Por qué se cerró. Queda en la consola para no tener que adivinarlo. */
  motivo = "sin motivo registrado"
) => {
  if (cerrandoSesion) return;
  cerrandoSesion = true;

  console.warn(`[sesión] se cerró la sesión: ${motivo}`);

  localStorage.clear();
  ObjNotificaciones.MSG_ERROR("warning", titulo, detalle);
  setTimeout(() => {
    window.location.href = RUTA_LOGIN;
  }, ESPERA_ANTES_DEL_LOGIN_MS);
};

/**
 * Interceptor de respuesta de los clientes de API. Deja pasar el error siempre: lo único que añade
 * es cerrar la sesión cuando consta que el token fue rechazado y hay un usuario dentro.
 */
export const manejarSesionRechazada = async (error: any) => {
  const hayUsuario = !!localStorage.getItem("user");

  // Durante las exportaciones masivas no se interrumpe al usuario a mitad del proceso.
  if (hayUsuario && !exportState.suppressRedirect && esSesionRechazada(error)) {
    const token = localStorage.getItem("token");

    // Si no hay token o ya venció según su propia fecha, se cierra la sesión directamente.
    if (!token || tokenVencido(token)) {
      cerrarSesionYAvisar(
        undefined,
        undefined,
        `token ausente o vencido en ${error?.config?.url}`
      );
      return Promise.reject(error);
    }

    // Antes de expulsar al usuario, se confirma con el servicio de autenticación si la sesión
    // realmente no sirve. Un endpoint secundario (jurados, documentos, etc.) puede responder 401/400
    // por falta de asignación, criterios no disponibles o permisos de recurso, sin que la sesión haya caducado.
    try {
      const vigencia = await confirmarVigencia(token);
      if (vigencia?.status === 400) {
        cerrarSesionYAvisar(
          undefined,
          undefined,
          `el servicio de usuarios confirmó que el token no sirve (400) tras error en ${error?.config?.url}`
        );
      } else {
        console.warn(
          `[sesión] Petición rechazada (${error?.response?.status}) en ${error?.config?.url}, pero la sesión de usuario sigue activa en autenticación.`
        );
      }
    } catch (errConfirmacion) {
      console.warn(
        `[sesión] No se pudo confirmar la vigencia del token tras error en ${error?.config?.url}; se mantiene la sesión.`,
        errConfirmacion
      );
    }
  }

  return Promise.reject(error);
};
